/**
 * FloatingNotesTasks.tsx — personal Notes & Tasks panel.
 *
 * Structurally mirrors FloatingChat.tsx (drag/resize/position+size
 * persistence/imperative-handle-ref pattern, shared ResizeHandle for the
 * visible SE affordance) — per design decision, the real Team Chat modal's
 * values win over the external reference.html handoff wherever they
 * conflict (header background #7BCBBA not #4BBFBB, 20px wordmark not 17px,
 * /relay-mark.svg image not an inlined all-white path, muted text #6B6B65
 * not #8A8578, tealDark #054750 hardcoded locally — NOT the global CSS
 * var(--teal-dark), which is a different color, #1A6B6B).
 *
 * No client-side Firestore access — see src/lib/notesTasksApi.ts and
 * server/api/notesTasks.ts for why (relay.git firestore.rules doesn't
 * reliably deploy to production). Data is fetched once when the panel is
 * first opened, then updated optimistically in local state with each
 * mutation persisted via the API layer in the background.
 */

import { useEscapeToClose } from "../hooks/useEscapeToClose";
import { centerWindow, clampWindow, isTitleBarTarget } from "../lib/windowBounds";
import { useCallback, useEffect, useRef, useState } from "react";
import { ResizeHandle } from "./ResizeHandle";
import { onUserChange } from "../lib/auth";
import {
  fetchNotesTasksData,
  createTaskCategory,
  deleteTaskCategory,
  createTaskItem,
  updateTaskItem,
  deleteTaskItem,
  reorderTaskItem,
  createNoteCategory,
  deleteNoteCategory,
  createNote,
  updateNote,
  deleteNote,
  type TaskCategory,
  type TaskItem,
  type NoteCategory,
  type Note,
  type Priority,
} from "../lib/notesTasksApi";

/* Design tokens — copied from FloatingChat.tsx's MC object (the real
 * modal wins over the handoff doc's approximated palette), plus berry,
 * which the chat modal doesn't need but this feature does (priority dots,
 * category color rotation). */
const MC = {
  teal: "#5FC4C0",
  tealLight: "#7BCBBA", // "mint" in the design brief's color table
  tealDark: "#054750",
  yellow: "#F5D347",
  orange: "#F4A460",
  berry: "#A8556B",
  paper: "#FFFFFF",
  ink: "#111111",
  inkSoft: "#3D3D3D",
  inkMuted: "#6B6B65",
  line: "#E6E2D4",
};

const CAT_COLORS = ["#054750", "#F4A460", "#7BCBBA", "#EED249", "#A8556B", "#5FC4C0", "#8A6B3F"];
const PRIORITY_COLOR: Record<Priority, string> = { high: MC.berry, medium: MC.orange, low: MC.tealLight };

const DEFAULT_SIZE = { w: 340, h: 460 };
const MIN_SIZE = { w: 280, h: 320 };

function fmtTimestamp(ms: number | null): string {
  if (!ms) return "";
  const diffMin = Math.floor((Date.now() - ms) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export interface FloatingNotesTasksProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* ───────────────────────── Checkbox ───────────────────────── */
function Checkbox({ done, onChange }: { done: boolean; onChange: () => void }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", transition: "all 150ms",
        border: `1.5px solid ${done ? MC.teal : MC.line}`,
        background: done ? MC.teal : "none",
      }}
    >
      {done && (
        <svg width="10" height="8" viewBox="0 0 10 8">
          <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

/* ───────────────────────── Tasks tab ───────────────────────── */
interface TasksTabProps {
  categories: TaskCategory[];
  items: TaskItem[];
  onCategoriesChange: (cats: TaskCategory[]) => void;
  onItemsChange: (items: TaskItem[]) => void;
}

function TasksTab({ categories, items, onCategoriesChange, onItemsChange }: TasksTabProps) {
  const [dragItem, setDragItem] = useState<{ catId: string; idx: number } | null>(null);
  const [dragOver, setDragOver] = useState<{ catId: string; idx: number } | null>(null);
  const [newItemCat, setNewItemCat] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState("");
  const [newCatMode, setNewCatMode] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<string | null>(null);
  // Mirrors NotesTab's filterCat — same category filter-pill pattern for
  // consistency. null/"All" shows every category's tasks, clustered by
  // category (not truly interleaved) so numbering/drag-reorder stays
  // scoped per category exactly like it was before this filter existed.
  const [filterCat, setFilterCat] = useState<string | null>(null);
  const newItemRef = useRef<HTMLInputElement>(null);
  const newCatRef = useRef<HTMLInputElement>(null);

  // Sorted by `order` (not array insertion order) — the 1/2/3 numbering
  // below is derived from this position, so it must reflect true order.
  const itemsByCat = useCallback((catId: string) => items.filter((i) => i.catId === catId).sort((a, b) => a.order - b.order), [items]);

  const toggleDone = (item: TaskItem) => {
    onItemsChange(items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)));
    updateTaskItem(item.id, { done: !item.done }).catch((e) => console.error("[notes-tasks] toggleDone failed:", e));
  };

  const cyclePriority = (item: TaskItem) => {
    const next: Priority = item.priority === "high" ? "medium" : item.priority === "medium" ? "low" : "high";
    onItemsChange(items.map((i) => (i.id === item.id ? { ...i, priority: next } : i)));
    updateTaskItem(item.id, { priority: next }).catch((e) => {
      console.error("[notes-tasks] cyclePriority failed:", e);
      onItemsChange(items);
    });
  };

  const deleteItem = (item: TaskItem) => {
    onItemsChange(items.filter((i) => i.id !== item.id));
    deleteTaskItem(item.id).catch((e) => console.error("[notes-tasks] deleteItem failed:", e));
  };

  const startNewItem = (catId: string) => {
    setNewItemCat(catId);
    setNewItemText("");
    setTimeout(() => newItemRef.current?.focus(), 50);
  };

  const commitNewItem = async (catId: string) => {
    const text = newItemText.trim();
    setNewItemCat(null);
    setNewItemText("");
    if (!text) return;
    try {
      const created = await createTaskItem({ text, catId, priority: "medium" });
      onItemsChange([...items, created]);
    } catch (e) {
      console.error("[notes-tasks] createTaskItem failed:", e);
    }
  };

  const addCategory = async () => {
    const name = newCatName.trim();
    setNewCatMode(false);
    setNewCatName("");
    if (!name) return;
    try {
      const created = await createTaskCategory({ name, color: CAT_COLORS[categories.length % CAT_COLORS.length] });
      onCategoriesChange([...categories, created]);
    } catch (e) {
      console.error("[notes-tasks] createTaskCategory failed:", e);
    }
  };

  // Deleting a task category cascades to delete its tasks too (server does
  // a batch delete) — real data loss, unlike note categories below, hence
  // the confirm step this file otherwise doesn't use for deletes.
  const deleteCategory = (cat: TaskCategory) => {
    onCategoriesChange(categories.filter((c) => c.id !== cat.id));
    onItemsChange(items.filter((i) => i.catId !== cat.id));
    setConfirmDeleteCat(null);
    if (filterCat === cat.id) setFilterCat(null);
    deleteTaskCategory(cat.id).catch((e) => console.error("[notes-tasks] deleteTaskCategory failed:", e));
  };

  const onDrop = (catId: string) => {
    if (!dragItem || dragItem.catId !== catId) return;
    const catItems = itemsByCat(catId);
    const reordered = [...catItems];
    const [moved] = reordered.splice(dragItem.idx, 1);
    const targetIdx = dragOver?.idx ?? reordered.length;
    reordered.splice(targetIdx, 0, moved);
    const movedIdx = reordered.findIndex((i) => i.id === moved.id);
    // Computed synchronously so local state can sort correctly right away —
    // see reorderTaskItem's doc comment.
    const newOrder = reorderTaskItem(reordered, movedIdx);
    const finalReordered = reordered.map((i) => (i.id === moved.id ? { ...i, order: newOrder } : i));
    const otherItems = items.filter((i) => i.catId !== catId);
    onItemsChange([...otherItems, ...finalReordered]);
    setDragItem(null);
    setDragOver(null);
  };

  // Same category filter-pill pattern as NotesTab, for consistency. null/"All"
  // shows every category's tasks, still clustered by category below (not
  // interleaved) so numbering/drag-reorder stays scoped per category.
  const visibleCats = filterCat ? categories.filter((c) => c.id === filterCat) : categories;
  const confirmCat = confirmDeleteCat ? categories.find((c) => c.id === confirmDeleteCat) : null;

  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "8px 14px 0", borderBottom: `1px solid ${MC.line}`, flexShrink: 0 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8, alignItems: "center" }}>
          <span
            onClick={() => setFilterCat(null)}
            style={{
              padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer",
              border: `1.5px solid ${filterCat === null ? MC.teal : MC.line}`,
              background: filterCat === null ? "rgba(95,196,192,.12)" : "transparent",
              color: filterCat === null ? MC.tealDark : MC.inkMuted,
            }}
          >
            All
          </span>
          {categories.map((c) => {
            const active2 = filterCat === c.id;
            return (
              <span
                key={c.id}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "3px 6px 3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer",
                  border: `1.5px solid ${c.color}`, background: active2 ? c.color : "transparent", color: active2 ? "#fff" : c.color,
                }}
              >
                <span onClick={() => setFilterCat(active2 ? null : c.id)}>{c.name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteCat(c.id); }}
                  title={`Delete category ${c.name}`}
                  aria-label={`Delete category ${c.name}`}
                  style={{ border: "none", background: "none", cursor: "pointer", color: MC.berry, fontSize: 22, lineHeight: 1, padding: "0 1px" }}
                >
                  ×
                </button>
              </span>
            );
          })}
          {newCatMode ? (
            <input
              ref={newCatRef}
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCategory();
                if (e.key === "Escape") { setNewCatMode(false); setNewCatName(""); }
              }}
              onBlur={addCategory}
              placeholder="Category…"
              style={{ border: `1px solid ${MC.teal}`, borderRadius: 99, padding: "2px 8px", fontSize: 10, fontFamily: "var(--font-ui)", outline: "none", width: 90 }}
            />
          ) : (
            <span
              onClick={() => { setNewCatMode(true); setTimeout(() => newCatRef.current?.focus(), 40); }}
              style={{ padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer", border: `1.5px dashed ${MC.line}`, color: MC.inkMuted }}
            >
              + category
            </span>
          )}
        </div>
      </div>
      {confirmCat && (
        // Deleting a task category cascades to delete its tasks too (server does a
        // batch delete) — real data loss, unlike note categories, hence a confirm
        // step here even though the pill-row delete affordance itself now matches
        // NotesTab's instant-delete ×.
        <div style={{ padding: "7px 14px", background: "#FDECEC", borderBottom: `1px solid ${MC.line}`, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11.5, color: MC.inkMuted, flex: 1 }}>
            Delete "{confirmCat.name}" and its {itemsByCat(confirmCat.id).length} task{itemsByCat(confirmCat.id).length !== 1 ? "s" : ""}?
          </span>
          <button onClick={() => deleteCategory(confirmCat)} style={{ border: "none", background: "none", cursor: "pointer", color: MC.berry, fontSize: 11, fontWeight: 700, padding: "0 4px" }}>Delete</button>
          <button onClick={() => setConfirmDeleteCat(null)} style={{ border: "none", background: "none", cursor: "pointer", color: MC.inkMuted, fontSize: 11, fontWeight: 600, padding: "0 4px" }}>Cancel</button>
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {visibleCats.map((cat) => {
          const catItems = itemsByCat(cat.id);
          const doneCount = catItems.filter((i) => i.done).length;
          return (
            <div key={cat.id} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(cat.id)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px 6px", position: "sticky", top: 0, background: "#FAFAF7", zIndex: 2, borderBottom: `1px solid ${MC.line}` }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, background: cat.color }} />
                <span style={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 14, color: MC.tealDark, flex: 1 }}>{cat.name}</span>
                <span style={{ fontSize: 11, color: MC.inkMuted, fontWeight: 500 }}>{doneCount}/{catItems.length}</span>
                <button onClick={() => startNewItem(cat.id)} title="Add item" style={{ border: "none", background: "none", cursor: "pointer", color: MC.teal, fontSize: 16, lineHeight: 1, padding: "0 2px" }}>+</button>
              </div>
              {catItems.map((item, idx) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => setDragItem({ catId: cat.id, idx })}
                  onDragEnter={() => setDragOver({ catId: cat.id, idx })}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 14px 7px 28px", transition: "background 120ms",
                    background: dragOver?.catId === cat.id && dragOver?.idx === idx ? "#EBF6F5" : "transparent",
                  }}
                >
                  <span style={{ color: MC.tealDark, fontSize: 14, cursor: "grab", flexShrink: 0, paddingTop: 1, lineHeight: 1 }}>⋮⋮</span>
                  <span style={{ width: 14, flexShrink: 0, textAlign: "right", fontSize: 10.5, color: MC.inkMuted, fontFamily: "JetBrains Mono, monospace", paddingTop: 2 }}>{idx + 1}</span>
                  <Checkbox done={item.done} onChange={() => toggleDone(item)} />
                  <span style={{ fontSize: 13, lineHeight: 1.45, flex: 1, color: item.done ? MC.inkMuted : MC.ink, textDecoration: item.done ? "line-through" : "none" }}>{item.text}</span>
                  <button
                    type="button"
                    onClick={() => cyclePriority(item)}
                    title={`${item.priority} priority — click to change`}
                    aria-label={`${item.priority} priority, click to change`}
                    style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, marginTop: 3, padding: 0, border: "none", cursor: "pointer", background: PRIORITY_COLOR[item.priority] }}
                  />
                  <button type="button" onClick={() => deleteItem(item)} title="Delete item" aria-label={`Delete ${item.text || "item"}`} style={{ border: "none", background: "none", cursor: "pointer", color: MC.berry, fontSize: 26, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              ))}
              {newItemCat === cat.id ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 14px 5px 28px" }}>
                  <div style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <input
                    ref={newItemRef}
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitNewItem(cat.id);
                      if (e.key === "Escape") { setNewItemCat(null); setNewItemText(""); }
                    }}
                    onBlur={() => commitNewItem(cat.id)}
                    placeholder="New item…"
                    style={{ border: "none", background: "none", fontFamily: "var(--font-ui)", fontSize: 13, color: MC.ink, outline: "none", flex: 1, padding: "4px 0", borderBottom: `1px solid ${MC.teal}` }}
                  />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 14px 5px 28px", opacity: 0.45, cursor: "pointer" }} onClick={() => startNewItem(cat.id)}>
                  <div style={{ width: 16, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: MC.inkMuted, fontStyle: "italic" }}>+ add item</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── Notes tab ───────────────────────── */
interface NotesTabProps {
  categories: NoteCategory[];
  notes: Note[];
  onCategoriesChange: (cats: NoteCategory[]) => void;
  onNotesChange: (notes: Note[]) => void;
}

function NotesTab({ categories, notes, onCategoriesChange, onNotesChange }: NotesTabProps) {
  const [filterCat, setFilterCat] = useState<string | null>(null);
  const [newCatMode, setNewCatMode] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const newCatRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // One pending patch per note: title and body edits merge into it so a single
  // write carries both, and it is flushed on note switch, close and unmount.
  const pendingRef = useRef<{ id: string; changes: { title?: string; body?: string } } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const active = notes.find((n) => n.id === activeId) ?? null;

  const addNoteCat = async () => {
    const name = newCatName.trim();
    setNewCatMode(false);
    setNewCatName("");
    if (!name) return;
    try {
      const created = await createNoteCategory({ name, color: CAT_COLORS[categories.length % CAT_COLORS.length] });
      onCategoriesChange([...categories, created]);
    } catch (e) {
      console.error("[notes-tasks] createNoteCategory failed:", e);
    }
  };

  // Unlike task categories, deleting a note category doesn't delete any
  // notes — the server unassigns catId back to null (notes survive), so
  // this stays a plain instant delete, matching this file's convention for
  // non-destructive actions (no confirm step).
  const removeCategory = (cat: NoteCategory) => {
    onCategoriesChange(categories.filter((c) => c.id !== cat.id));
    onNotesChange(notes.map((n) => (n.catId === cat.id ? { ...n, catId: null } : n)));
    if (filterCat === cat.id) setFilterCat(null);
    deleteNoteCategory(cat.id).catch((e) => console.error("[notes-tasks] deleteNoteCategory failed:", e));
  };

  const flushPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    updateNote(pending.id, pending.changes).catch((e) => {
      console.error("[notes-tasks] updateNote failed:", e);
      setSaveError("Couldn't save your last change. Check your connection and edit the note again.");
    });
  }, []);

  // Cleanup runs when the open note changes and when the tab unmounts (panel
  // closed, tab switched, app unmounted), so a pending edit is never dropped.
  useEffect(() => () => flushPending(), [activeId, flushPending]);

  const reportSaveFailure = (what: string) => (e: unknown) => {
    console.error(`[notes-tasks] ${what} failed:`, e);
    setSaveError("Couldn't save your change. Check your connection and try again.");
  };

  const newNote = async () => {
    try {
      const created = await createNote({ title: "", body: "", catId: filterCat });
      onNotesChange([created, ...notes]);
      setActiveId(created.id);
    } catch (e) {
      console.error("[notes-tasks] createNote failed:", e);
    }
  };

  /** Local update is immediate; persisted write is debounced 500ms per the
   * spec — avoids a Firestore write per keystroke. */
  const updateLocal = (id: string, field: "title" | "body", val: string) => {
    onNotesChange(notes.map((n) => (n.id === id ? { ...n, [field]: val } : n)));
    if (pendingRef.current && pendingRef.current.id !== id) flushPending();
    pendingRef.current = { id, changes: { ...pendingRef.current?.changes, [field]: val } };
    setSaveError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushPending, 500);
  };

  const setCatId = (id: string, catId: string | null) => {
    onNotesChange(notes.map((n) => (n.id === id ? { ...n, catId } : n)));
    updateNote(id, { catId }).catch(reportSaveFailure("setCatId"));
  };

  const togglePin = (n: Note) => {
    onNotesChange(notes.map((x) => (x.id === n.id ? { ...x, pinned: !x.pinned } : x)));
    updateNote(n.id, { pinned: !n.pinned }).catch(reportSaveFailure("togglePin"));
  };

  const removeNote = (id: string) => {
    onNotesChange(notes.filter((n) => n.id !== id));
    if (pendingRef.current?.id === id) pendingRef.current = null;
    if (activeId === id) setActiveId(null);
    deleteNote(id).catch(reportSaveFailure("deleteNote"));
  };

  const autoExpand = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    if (bodyRef.current) autoExpand(bodyRef.current);
  }, [activeId]);

  const filtered = filterCat ? notes.filter((n) => n.catId === filterCat) : notes;
  const sorted = [...filtered].sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.createdAt ?? 0) - (a.createdAt ?? 0));

  if (active) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderBottom: `1px solid ${MC.line}`, background: "#FAFAF7", flexShrink: 0 }}>
          <button onClick={() => setActiveId(null)} style={{ border: "none", background: "none", cursor: "pointer", color: MC.teal, fontSize: 13, fontFamily: "var(--font-ui)", padding: 0 }}>← All notes</button>
          <div style={{ flex: 1 }} />
          <button onClick={() => togglePin(active)} title={active.pinned ? "Unpin" : "Pin"} style={{ border: "none", background: "none", cursor: "pointer", color: active.pinned ? MC.yellow : MC.line, fontSize: 15, padding: 0 }}>📌</button>
          <button type="button" onClick={() => removeNote(active.id)} title="Delete note" aria-label={`Delete ${active.title || "note"}`} style={{ border: "none", background: "none", cursor: "pointer", color: MC.tealDark, fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
          {saveError && (
            <div role="alert" style={{ fontSize: 12, color: MC.berry, marginBottom: 10 }}>{saveError}</div>
          )}
          <div style={{ fontSize: 10, color: MC.inkMuted, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${MC.line}` }}>
            {fmtTimestamp(active.createdAt)}
            {active.pinned && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: "#EBF6F5", color: MC.tealDark, marginLeft: 8 }}>📌 pinned</span>
            )}
          </div>
          <input
            value={active.title}
            onChange={(e) => updateLocal(active.id, "title", e.target.value)}
            placeholder="Note title…"
            style={{ border: "none", background: "none", fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 17, color: MC.tealDark, outline: "none", width: "100%", marginBottom: 8, letterSpacing: "-0.01em" }}
          />
          {categories.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: MC.inkMuted, alignSelf: "center", marginRight: 2 }}>Category:</span>
              {categories.map((c) => {
                const assigned = active.catId === c.id;
                return (
                  <span
                    key={c.id}
                    onClick={() => setCatId(active.id, assigned ? null : c.id)}
                    style={{
                      display: "inline-block", padding: "2px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer",
                      border: `1.5px solid ${c.color}`, background: assigned ? c.color : "transparent", color: assigned ? "#fff" : c.color, transition: "all 150ms",
                    }}
                  >
                    {c.name}
                  </span>
                );
              })}
            </div>
          )}
          <textarea
            ref={bodyRef}
            value={active.body}
            onChange={(e) => { updateLocal(active.id, "body", e.target.value); autoExpand(e.target); }}
            placeholder="Start writing…"
            rows={1}
            style={{ border: "none", background: "none", fontFamily: "var(--font-ui)", fontSize: 13, color: MC.ink, outline: "none", width: "100%", resize: "none", lineHeight: 1.6, minHeight: 80 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "8px 14px 0", borderBottom: `1px solid ${MC.line}` }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8, alignItems: "center" }}>
          <span
            onClick={() => setFilterCat(null)}
            style={{
              padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer",
              border: `1.5px solid ${filterCat === null ? MC.teal : MC.line}`,
              background: filterCat === null ? "rgba(95,196,192,.12)" : "transparent",
              color: filterCat === null ? MC.tealDark : MC.inkMuted,
            }}
          >
            All
          </span>
          {categories.map((c) => {
            const active2 = filterCat === c.id;
            return (
              <span
                key={c.id}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "3px 6px 3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer",
                  border: `1.5px solid ${c.color}`, background: active2 ? c.color : "transparent", color: active2 ? "#fff" : c.color,
                }}
              >
                <span onClick={() => setFilterCat(active2 ? null : c.id)}>{c.name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeCategory(c); }}
                  title={`Delete category ${c.name}`}
                  aria-label={`Delete category ${c.name}`}
                  style={{ border: "none", background: "none", cursor: "pointer", color: MC.berry, fontSize: 22, lineHeight: 1, padding: "0 1px" }}
                >
                  ×
                </button>
              </span>
            );
          })}
          {newCatMode ? (
            <input
              ref={newCatRef}
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addNoteCat();
                if (e.key === "Escape") { setNewCatMode(false); setNewCatName(""); }
              }}
              onBlur={addNoteCat}
              placeholder="Category…"
              style={{ border: `1px solid ${MC.teal}`, borderRadius: 99, padding: "2px 8px", fontSize: 10, fontFamily: "var(--font-ui)", outline: "none", width: 90 }}
            />
          ) : (
            <span
              onClick={() => { setNewCatMode(true); setTimeout(() => newCatRef.current?.focus(), 40); }}
              style={{ padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, cursor: "pointer", border: `1.5px dashed ${MC.line}`, color: MC.inkMuted }}
            >
              + category
            </span>
          )}
        </div>
      </div>
      <div style={{ padding: "6px 14px 4px", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={newNote} style={{ border: "none", background: MC.orange, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#fff", cursor: "pointer", fontFamily: "var(--font-ui)" }}>
          + New note
        </button>
      </div>
      {saveError && (
        <div role="alert" style={{ padding: "6px 14px", fontSize: 12, color: MC.berry }}>{saveError}</div>
      )}
      {sorted.length === 0 && <div style={{ padding: "32px 14px", textAlign: "center", color: MC.inkMuted, fontSize: 13 }}>No notes yet</div>}
      {sorted.map((n) => {
        const cat = n.catId ? categories.find((c) => c.id === n.catId) : null;
        return (
          <div key={n.id} onClick={() => setActiveId(n.id)} style={{ padding: "9px 14px", cursor: "pointer", borderBottom: `1px solid ${MC.line}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: MC.tealDark, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
              {n.pinned && <span style={{ color: MC.yellow, fontSize: 11 }}>📌</span>}
              <span style={{ flex: 1 }}>{n.title || <span style={{ color: MC.inkMuted, fontStyle: "italic" }}>Untitled</span>}</span>
              <span style={{ fontSize: 10, color: MC.inkMuted, fontWeight: 400 }}>{fmtTimestamp(n.createdAt)}</span>
            </div>
            {cat && (
              <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 99, fontSize: 9, fontWeight: 700, marginTop: 3, background: `${cat.color}22`, color: cat.color }}>
                {cat.name}
              </span>
            )}
            <div style={{ fontSize: 11, color: MC.inkMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {n.body || <span style={{ fontStyle: "italic" }}>Empty note</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────────── Main panel ─────────────────────────
 * `open` is controlled by the parent (App.tsx) so the "Notes & Tasks" nav
 * row can show an active state while the panel is open, matching every
 * other nav row's behavior — the FAB and header close button just call
 * onOpenChange rather than owning their own boolean. */
export function FloatingNotesTasks({ open, onOpenChange }: FloatingNotesTasksProps) {
  const [tab, setTab] = useState<"tasks" | "notes">("tasks");
  const [signedIn, setSignedIn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [fabHover, setFabHover] = useState(false);

  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.nt.pos") || "null");
      if (v && typeof v.x === "number") return v;
    } catch {}
    return null;
  });
  const [size, setSize] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.nt.size") || "null");
      if (v && typeof v.w === "number") return v;
    } catch {}
    return DEFAULT_SIZE;
  });

  const [taskCategories, setTaskCategories] = useState<TaskCategory[]>([]);
  const [taskItems, setTaskItems] = useState<TaskItem[]>([]);
  const [noteCategories, setNoteCategories] = useState<NoteCategory[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);

  useEffect(() => onUserChange((u) => setSignedIn(!!u)), []);

  // Fetch once, the first time the panel is opened by a signed-in user.
  useEffect(() => {
    if (!open || !signedIn || loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchNotesTasksData();
        if (cancelled) return;
        setTaskCategories(data.taskCategories);
        setTaskItems(data.taskItems);
        setNoteCategories(data.noteCategories);
        setNotes(data.notes);
        setLoadError(null);
        setLoaded(true);
      } catch (e) {
        console.error("[notes-tasks] initial fetch failed:", e);
        if (!cancelled) setLoadError("Couldn't load your notes and tasks.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, signedIn, loaded, loadAttempt]);

  useEffect(() => {
    if (pos) localStorage.setItem("relay.nt.pos", JSON.stringify(pos));
  }, [pos]);
  useEffect(() => {
    localStorage.setItem("relay.nt.size", JSON.stringify(size));
  }, [size]);

  const defaultPos = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return { x: Math.max(16, vw - size.w - 32), y: Math.max(16, vh - size.h - 32) };
  }, [size.w, size.h]);

  // A saved or dragged position can never leave the title bar off-screen: correct it on
  // load (the persistence effect saves it back) and whenever the browser window resizes.
  useEffect(() => {
    const reclamp = () => setPos((cur) => (cur ? clampWindow({ ...cur, width: size.w, height: size.h }) : cur));
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [size.w, size.h]);

  // Double-click the title bar (not a control inside it) to return to the default size and position.
  const recenter = useCallback((e: React.MouseEvent) => {
    if (!isTitleBarTarget(e.target)) return;
    const c = centerWindow({ width: DEFAULT_SIZE.w, height: DEFAULT_SIZE.h });
    setSize(DEFAULT_SIZE);
    setPos({ x: c.x, y: c.y });
  }, []);

  useEscapeToClose(open, () => onOpenChange(false));

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!pos) setPos(defaultPos());
      const startX = e.clientX;
      const startY = e.clientY;
      const p = pos || defaultPos();
      const ox = p.x;
      const oy = p.y;
      const onMove = (ev: MouseEvent) => {
        const { x: nx, y: ny } = clampWindow({ x: ox + (ev.clientX - startX), y: oy + (ev.clientY - startY), width: size.w, height: size.h });
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos, defaultPos, size.w, size.h]
  );

  const startResize = useCallback(
    (e: React.MouseEvent, dir: string) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const sw = size.w;
      const sh = size.h;
      const p = pos || defaultPos();
      const ox = p.x;
      const oy = p.y;
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let nw = sw;
        let nh = sh;
        let nx = ox;
        let ny = oy;
        if (dir.includes("e")) nw = Math.max(MIN_SIZE.w, sw + dx);
        if (dir.includes("w")) { nw = Math.max(MIN_SIZE.w, sw - dx); nx = ox + (sw - nw); }
        if (dir.includes("s")) nh = Math.max(MIN_SIZE.h, sh + dy);
        if (dir.includes("n")) { nh = Math.max(MIN_SIZE.h, sh - dy); ny = oy + (sh - nh); }
        setSize({ w: nw, h: nh });
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [size, pos, defaultPos]
  );

  if (!signedIn) return null;

  const p = pos || defaultPos();
  const pendingTasks = taskItems.filter((i) => !i.done).length;
  const totalNotes = notes.length;

  return (
    <>
      {/* FAB — shown only when closed, per spec */}
      {!open && (
        <button
          onClick={() => onOpenChange(true)}
          onMouseEnter={() => setFabHover(true)}
          onMouseLeave={() => setFabHover(false)}
          aria-label="Open Notes & Tasks"
          style={{
            position: "fixed", bottom: 28, right: 28, width: 50, height: 50, borderRadius: "50%",
            background: fabHover ? "#0A6E72" : MC.tealDark, border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(5,71,80,.35)", zIndex: 190,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          {pendingTasks > 0 && (
            <div style={{ position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, borderRadius: 9, background: MC.orange, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", fontFamily: "var(--font-ui)" }}>
              {pendingTasks}
            </div>
          )}
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Notes and Tasks"
          style={{
            position: "fixed", left: p.x, top: p.y, width: size.w, height: size.h,
            background: MC.paper, borderRadius: 16, border: `1.5px solid ${MC.line}`,
            boxShadow: "0 8px 48px rgba(5,71,80,.2), 0 2px 8px rgba(5,71,80,.1)",
            display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 200, fontFamily: "var(--font-ui)",
          }}
        >
          {/* Header rail — matches FloatingChat's real header exactly */}
          <div
            onMouseDown={startDrag}
            onDoubleClick={recenter}
            style={{
              background: MC.tealLight, borderBottom: `1px solid ${MC.teal}`, borderTopLeftRadius: 15, borderTopRightRadius: 15,
              padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, cursor: "move", userSelect: "none", flexShrink: 0,
            }}
          >
            <img src="/relay-mark.svg" style={{ width: 25, height: 25, display: "block" }} alt="" />
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <span style={{ fontFamily: "var(--font-mark)", fontSize: 20, fontWeight: 400, letterSpacing: "-0.015em", color: MC.tealDark, lineHeight: 1.1 }}>relay</span>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: 9, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: MC.tealDark, opacity: 0.75, lineHeight: 1.2 }}>
                NOTES &amp; TASKS
              </span>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              title="Close"
              style={{
                background: "transparent",
                border: "none",
                color: MC.tealDark,
                fontSize: 24,
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${MC.line}`, background: "#FAFAF7", flexShrink: 0 }}>
            {(
              [
                ["tasks", `Tasks${pendingTasks ? ` (${pendingTasks})` : ""}`, MC.teal, "rgba(95,196,192,.08)"],
                ["notes", `Notes${totalNotes ? ` (${totalNotes})` : ""}`, MC.orange, "rgba(244,164,96,.08)"],
              ] as const
            ).map(([id, label, accentColor, accentBg]) => {
              const isActive = tab === id;
              return (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  style={{
                    flex: 1, padding: "7px 10px", borderRadius: 99, background: isActive ? accentBg : "none", cursor: "pointer",
                    fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
                    border: `1.5px solid ${isActive ? accentColor : MC.line}`, color: isActive ? (id === "tasks" ? MC.tealDark : MC.orange) : MC.inkMuted,
                    transition: "all 150ms",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", scrollbarWidth: "thin", scrollbarColor: `${MC.teal} transparent` }}>
            {!loaded && loadError ? (
              <div role="alert" style={{ padding: "32px 14px", textAlign: "center", color: MC.berry, fontSize: 13 }}>
                <div style={{ marginBottom: 10 }}>{loadError}</div>
                <button type="button" onClick={() => { setLoadError(null); setLoadAttempt((n) => n + 1); }} style={{ border: `1.5px solid ${MC.teal}`, background: "none", borderRadius: 99, padding: "5px 14px", cursor: "pointer", color: MC.tealDark, fontSize: 12, fontWeight: 600, fontFamily: "var(--font-ui)" }}>Retry</button>
              </div>
            ) : !loaded ? (
              <div style={{ padding: "32px 14px", textAlign: "center", color: MC.inkMuted, fontSize: 13 }}>Loading…</div>
            ) : tab === "tasks" ? (
              <TasksTab categories={taskCategories} items={taskItems} onCategoriesChange={setTaskCategories} onItemsChange={setTaskItems} />
            ) : (
              <NotesTab categories={noteCategories} notes={notes} onCategoriesChange={setNoteCategories} onNotesChange={setNotes} />
            )}
          </div>

          {/* Resize handles — all 8 directions, same pattern as FloatingChat.tsx */}
          {(["nw", "ne", "sw"] as const).map((dir) => {
            const cursors: Record<string, string> = { nw: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize" };
            const posStyle: Record<string, React.CSSProperties> = { nw: { top: 0, left: 0 }, ne: { top: 0, right: 0 }, sw: { bottom: 0, left: 0 } };
            return (
              <div
                key={dir}
                role="button"
                aria-label={`Resize ${dir}`}
                onMouseDown={(e) => startResize(e, dir)}
                style={{ position: "absolute", width: 14, height: 14, cursor: cursors[dir], zIndex: 10, ...posStyle[dir] }}
              />
            );
          })}
          <ResizeHandle onMouseDown={(e) => startResize(e, "se")} style={{ zIndex: 10 }} />
          {(["n", "s", "e", "w"] as const).map((dir) => {
            const cursors: Record<string, string> = { n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize" };
            const edgeStyle: Record<string, React.CSSProperties> = {
              n: { top: 0, left: 14, right: 14, height: 6 },
              s: { bottom: 0, left: 14, right: 14, height: 6 },
              e: { right: 0, top: 14, bottom: 14, width: 6 },
              w: { left: 0, top: 14, bottom: 14, width: 6 },
            };
            return (
              <div
                key={dir}
                role="button"
                aria-label={`Resize ${dir}`}
                onMouseDown={(e) => startResize(e, dir)}
                style={{ position: "absolute", cursor: cursors[dir], zIndex: 9, ...edgeStyle[dir] }}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

export default FloatingNotesTasks;
